import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API_BASE, extractErrorMessage } from "@/lib/api";
import { useLang } from "@/i18n";
import { LANGUAGES } from "@/i18n/languages";
import { Download, Search, Save } from "lucide-react";
import { toast } from "sonner";
import { ViewScopeTabs, viewToParams } from "@/components/admin/ViewScopeTabs";
import {
  InternalControlFieldsDrawer,
  ProfileDataCompletenessCell,
  ProfileDataIntelligenceBanner,
} from "@/components/admin/ProfileDataP2";

const STATUSES = ["new", "contacted", "qualified", "pilot_ready", "converted", "dropped"];
const FOLLOW_UP = ["pending", "in_progress", "scheduled", "done"];

const AdminUsers = () => {
  const { t, lang, pickI18n, tProfile, tCountry } = useLang();
  // RL-N5 BUG fix (May 2026) — Accept a `?q=` URL parameter so deep links
  // from other admin surfaces (e.g. Racket Lab ContextCard's "View" link)
  // can land on this page with the search box pre-filled, instead of
  // pointing at a non-existent /admin/users/:id route. The query value
  // becomes the initial value of `filters.search`; further user edits in
  // the search box behave exactly as before (no URL re-sync, kept simple).
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get("q") || "";
  const [users, setUsers] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ profile_key: "", country: "", lead_status: "", pilot_ready: "", consent_marketing: "", email_verified: "", language: "", search: initialSearch });
  // Phase A17-B — 3-state view scope: "production" | "test" | "all".
  const [viewScope, setViewScope] = useState("production");
  const [editing, setEditing] = useState(null);
  // Phase A17-G — bulk admin-controlled moderation state.
  const [selectedIds, setSelectedIds] = useState(new Set());
  // The bulk-action dropdown defaults to "inactive" — visual only, never
  // applied until the admin clicks "Apply". A confirmation modal further
  // protects against accidental changes.
  const [bulkAction, setBulkAction] = useState("inactive");
  // Phase A17-J — structured audit reason dropdown + optional "other"
  // free-text. Internal codes are kept stable (never localized).
  const [bulkReasonCode, setBulkReasonCode] = useState("internal_test_email");
  // Settings/Profile Data Phase 2 — admin intelligence widget state.
  // `intelligence.banner` feeds the header KPIs, `intelligence.users`
  // is a Map keyed by user_id for O(1) per-row lookup.
  const [intelligence, setIntelligence] = useState(null);
  const [intelligenceByUser, setIntelligenceByUser] = useState({});
  const [icfUserId, setIcfUserId] = useState(null);
  const [bulkReasonOther, setBulkReasonOther] = useState("");
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  // Preview counts (filled by /admin/users/moderate/preview before apply).
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkPreviewLoading, setBulkPreviewLoading] = useState(false);

  // Phase A17-J — stable internal codes; UI label resolved at render.
  const REASON_CODES = [
    "internal_test_email",
    "wrong_email",
    "reported_deactivation_reregistration",
    "unwanted_client_email",
    "other",
  ];

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const all = new Set(users.map((u) => u.id));
      // If everything visible is already selected, clear; otherwise select all.
      const allChecked = users.length > 0 && users.every((u) => prev.has(u.id));
      if (allChecked) return new Set();
      return all;
    });
  };
  const cancelBulk = () => {
    setSelectedIds(new Set());
    setBulkAction("inactive");
    setBulkReasonCode("internal_test_email");
    setBulkReasonOther("");
    setBulkConfirmOpen(false);
    setBulkPreview(null);
  };
  const openBulkConfirm = async () => {
    if (selectedIds.size === 0) return;
    setBulkPreviewLoading(true);
    setBulkPreview(null);
    setBulkConfirmOpen(true);
    try {
      const { data } = await api.post("/admin/users/moderate/preview", {
        user_ids: Array.from(selectedIds),
        status: bulkAction,
      });
      setBulkPreview(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || String(err));
      setBulkConfirmOpen(false);
    } finally {
      setBulkPreviewLoading(false);
    }
  };
  const applyBulk = async () => {
    if (selectedIds.size === 0) return;
    // Phase A17-J — validate "other" requires text; predefined codes do not.
    if (bulkReasonCode === "other" && !bulkReasonOther.trim()) {
      toast.error(t("phase2.bulkReasonOtherRequired"));
      return;
    }
    setBulkSubmitting(true);
    try {
      const { data } = await api.post("/admin/users/moderate", {
        user_ids: Array.from(selectedIds),
        status: bulkAction,
        reason_code: bulkReasonCode,
        custom_reason_text: bulkReasonCode === "other" ? bulkReasonOther.trim() : null,
      });
      toast.success(t("phase2.bulkModerationApplied").replace("{n}", data.expanded_user_count ?? selectedIds.size));
      cancelBulk();
      await fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || String(err));
    } finally {
      setBulkSubmitting(false);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== null) params[k] = v; });
    Object.assign(params, viewToParams(viewScope));
    const { data } = await api.get("/admin/users", { params });
    setUsers(data);
    setLoading(false);
  };

  useEffect(() => {
    api.get("/profiles").then((r) => setProfiles(r.data));
    api.get("/countries").then((r) => setCountries(r.data));
    // Settings P2 — load intelligence aggregate once. The banner is
    // overall (no filter), per-row data is looked up by user_id.
    api.get("/admin/users/profile-data-intelligence?limit=2000")
      .then((r) => {
        setIntelligence(r.data);
        const byUser = {};
        (r.data.users || []).forEach((u) => { byUser[u.user_id] = u; });
        setIntelligenceByUser(byUser);
      })
      .catch(() => { /* non-fatal: widget just stays empty */ });
  }, []);
  useEffect(() => { fetchUsers(); /* eslint-disable-next-line */ }, [filters, viewScope]);
  // Phase A17-G — when the view scope changes, drop any pending selection
  // so we never apply a bulk action to rows the admin can no longer see.
  useEffect(() => { setSelectedIds(new Set()); }, [viewScope]);

  // Country dropdown is alphabetical by the localized label.
  const sortedCountries = useMemo(() => {
    const collator = new Intl.Collator(lang || "en", { sensitivity: "base" });
    return [...countries].sort((a, b) => {
      const an = tCountry(a.code, a);
      const bn = tCountry(b.code, b);
      return collator.compare(an, bn);
    });
  }, [countries, lang, tCountry]);

  const updateUser = async (uid, patch) => {
    try {
      await api.patch(`/admin/users/${uid}`, patch);
      setUsers((arr) => arr.map((u) => u.id === uid ? { ...u, ...patch } : u));
      toast.success(t("common.saved"));
    } catch (e) { toast.error(extractErrorMessage(e)); }
  };

  const downloadCsv = async () => {
    const token = localStorage.getItem("tsw_token");
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) params.set(k, v);
    });
    // Phase A17-B — export honours the same view scope as the table.
    const vp = viewToParams(viewScope);
    if (vp.only_test) params.set("only_test", "true");
    if (vp.include_test) params.set("include_test", "true");
    const qs = params.toString();
    const url = `${API_BASE}/admin/export/users.csv${qs ? `?${qs}` : ""}`;
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const text = await res.text();
        let detail = text;
        try { detail = JSON.parse(text)?.detail || text; } catch { /* ignore */ }
        throw new Error(`${res.status}: ${detail || t("apiFallback.exportFailed")}`);
      }
      const blob = await res.blob();
      const obj = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = obj;
      a.download = "teamsportworld_users.csv";
      a.click();
      URL.revokeObjectURL(obj);
    } catch (err) {
      toast.error(err?.message || t("apiFallback.exportFailed"));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="tsw-overline">{t("admin.title")}</div>
          <h1 className="font-display font-bold text-3xl text-slate-900">{t("admin.users")}</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <ViewScopeTabs value={viewScope} onChange={setViewScope} testIdPrefix="users-view-scope" />
          <button onClick={downloadCsv} className="tsw-btn-secondary" data-testid="users-export-csv">
            <Download className="w-4 h-4" /> {t("admin.exportUsers")}
          </button>
        </div>
      </div>

      {/* Settings/Profile Data Phase 2 — KPI banner. Renders only
          when the intelligence endpoint loaded. */}
      <ProfileDataIntelligenceBanner snapshot={intelligence} />

      <div className="tsw-card p-4 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="users-filters">
        <div className="relative col-span-1 sm:col-span-2 lg:col-span-2">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <input className="tsw-input ps-9" placeholder={t("phase2.searchPlaceholder")} value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} data-testid="users-search" />
        </div>
        <select className="tsw-input" value={filters.profile_key} onChange={(e) => setFilters((f) => ({ ...f, profile_key: e.target.value }))} data-testid="filter-profile">
          <option value="">{t("phase2.allProfiles")}</option>
          {profiles.map((p) => <option key={p.key} value={p.key}>{tProfile(p.key, p)}</option>)}
        </select>
        <select className="tsw-input" value={filters.country} onChange={(e) => setFilters((f) => ({ ...f, country: e.target.value }))} data-testid="filter-country">
          <option value="">{t("phase2.allCountries")}</option>
          {sortedCountries.map((c) => (
            <option key={c.code} value={c.code}>{tCountry(c.code, c)}</option>
          ))}
        </select>
        <select className="tsw-input" value={filters.lead_status} onChange={(e) => setFilters((f) => ({ ...f, lead_status: e.target.value }))} data-testid="filter-status">
          <option value="">{t("phase2.allStatuses")}</option>
          {STATUSES.map((s) => <option key={s} value={s}>{t(`leadStatuses.${s}`)}</option>)}
        </select>
        <select className="tsw-input" value={filters.pilot_ready} onChange={(e) => setFilters((f) => ({ ...f, pilot_ready: e.target.value }))} data-testid="filter-pilot-ready">
          <option value="">{t("phase2.pilotAny")}</option>
          <option value="true">{t("phase2.pilotReadyOpt")}</option>
          <option value="false">{t("phase2.pilotNotReady")}</option>
        </select>
        <select className="tsw-input" value={filters.language} onChange={(e) => setFilters((f) => ({ ...f, language: e.target.value }))} data-testid="filter-language">
          <option value="">{t("admin.byLanguage")}</option>
          {LANGUAGES.filter((L) => L.isActive).map((L) => (
            <option key={L.code} value={L.code}>{`${L.code.toUpperCase()} — ${L.nameNative}`}</option>
          ))}
        </select>
      </div>

      <div className="tsw-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-slate-50">
              <tr className="text-start text-xs text-slate-500 uppercase tracking-wider">
                <th className="px-3 py-3">
                  <input
                    type="checkbox"
                    aria-label={t("phase2.selectAllUsers")}
                    checked={users.length > 0 && users.every((u) => selectedIds.has(u.id))}
                    onChange={toggleSelectAll}
                    data-testid="users-select-all"
                  />
                </th>
                <th className="px-4 py-3">{t("admin.emailColumn")}</th>
                <th className="px-4 py-3">{t("auth.fullName")}</th>
                <th className="px-4 py-3">{t("auth.profile")}</th>
                <th className="px-4 py-3">{t("admin.countryColumn")}</th>
                <th className="px-4 py-3">{t("admin.languageColumn")}</th>
                <th
                  className="px-4 py-3 cursor-help"
                  title={t("admin.leadStatusTooltip")}
                  data-testid="users-col-lead-funnel"
                >
                  {t("admin.leadStatus")}
                </th>
                <th className="px-4 py-3">{t("admin.followUp")}</th>
                <th className="px-4 py-3">{t("admin.pilotReadyShort")}</th>
                <th className="px-4 py-3">{t("admin.interest")}</th>
                <th className="px-4 py-3">{t("admin.score")}</th>
                <th className="px-4 py-3">{t("admin.profileDataP2.columns.profileData")}</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const profileLbl = tProfile(u.profile_key, profiles.find((x) => x.key === u.profile_key));
                const countryLbl = u.country
                  ? tCountry(u.country, countries.find((x) => x.code === u.country))
                  : "—";
                return (
                <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`user-row-${u.id}`}>
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(u.id)}
                      onChange={() => toggleSelect(u.id)}
                      data-testid={`user-row-checkbox-${u.id}`}
                      aria-label={`Select ${u.email}`}
                    />
                  </td>
                  <td className="px-4 py-3 text-slate-900 font-medium">
                    {u.email}
                    {u.moderation_status && u.moderation_status !== "active" && (
                      <span
                        className={`ms-2 text-[10px] font-medium px-2 py-0.5 rounded-full ${
                          u.moderation_status === "spam"
                            ? "bg-rose-100 text-rose-700"
                            : u.moderation_status === "inactive"
                            ? "bg-slate-200 text-slate-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                        data-testid={`row-mod-badge-${u.id}`}
                      >
                        {u.moderation_status === "test_qa"
                          ? t("phase2.viewTestQa")
                          : u.moderation_status === "spam"
                          ? t("phase2.viewSpam")
                          : t("phase2.viewInactive")}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{u.full_name}</td>
                  <td className="px-4 py-3 text-slate-700 text-xs"><span className="tsw-chip bg-slate-100 text-slate-700" data-testid={`row-profile-${u.id}`} title={u.profile_key}>{profileLbl}</span></td>
                  <td className="px-4 py-3 text-slate-700">{countryLbl}</td>
                  <td className="px-4 py-3 text-slate-700"><span className="tsw-chip bg-slate-100 text-slate-700 uppercase">{u.language || "en"}</span></td>
                  <td className="px-4 py-3">
                    <select className="tsw-input py-1" value={u.lead_status || "new"} onChange={(e) => updateUser(u.id, { lead_status: e.target.value })} data-testid={`row-status-${u.id}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{t(`leadStatuses.${s}`)}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <select className="tsw-input py-1" value={u.follow_up_status || "pending"} onChange={(e) => updateUser(u.id, { follow_up_status: e.target.value })} data-testid={`row-followup-${u.id}`}>
                      {FOLLOW_UP.map((s) => <option key={s} value={s}>{t(`followUpStatuses.${s}`)}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <input type="checkbox" checked={!!u.pilot_ready} onChange={(e) => updateUser(u.id, { pilot_ready: e.target.checked })} data-testid={`row-pilot-${u.id}`} />
                  </td>
                  <td className="px-4 py-3">{u.interest_level ?? 0}</td>
                  <td className="px-4 py-3 font-semibold">{u.lead_score ?? 0}</td>
                  <td className="px-4 py-3">
                    <ProfileDataCompletenessCell
                      summary={intelligenceByUser[u.id]}
                      onOpen={() => setIcfUserId(u.id)}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => setEditing(u)} className="text-emerald-700 text-xs font-medium" data-testid={`row-edit-${u.id}`}>{t("common.edit")}</button>
                  </td>
                </tr>
                );
              })}
              {users.length === 0 && (
                <tr><td colSpan={13} className="px-4 py-10 text-center text-slate-400">{loading ? t("common.loading") : "—"}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Phase A17-G — floating bulk moderation action bar.
          Visible only when 1+ rows are selected. Default action is
          "inactive" (preselected) but NEVER applied until the admin
          confirms via the modal. Admin can cancel without changes. */}
      {selectedIds.size > 0 && (
        <div
          className="fixed left-1/2 -translate-x-1/2 bottom-6 z-40 bg-white shadow-xl rounded-2xl border border-slate-200 px-5 py-3 flex items-center gap-4 text-sm"
          data-testid="bulk-moderation-bar"
        >
          <span className="text-slate-700 font-medium">
            {t("phase2.bulkSelectedCount").replace("{n}", selectedIds.size)}
          </span>
          <select
            className="tsw-input py-1"
            value={bulkAction}
            onChange={(e) => setBulkAction(e.target.value)}
            data-testid="bulk-moderation-select"
          >
            <option value="active">{t("phase2.viewProduction")}</option>
            <option value="test_qa">{t("phase2.viewTestQa")}</option>
            <option value="spam">{t("phase2.viewSpam")}</option>
            <option value="inactive">{t("phase2.viewInactive")}</option>
          </select>
          <button
            type="button"
            className="px-4 py-1.5 bg-emerald-600 text-white rounded-full font-medium hover:bg-emerald-700"
            onClick={openBulkConfirm}
            data-testid="bulk-moderation-apply"
          >
            {t("phase2.bulkApply")}
          </button>
          <button
            type="button"
            className="px-4 py-1.5 bg-slate-100 text-slate-700 rounded-full hover:bg-slate-200"
            onClick={cancelBulk}
            data-testid="bulk-moderation-cancel"
          >
            {t("common.cancel")}
          </button>
        </div>
      )}

      {bulkConfirmOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setBulkConfirmOpen(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="bulk-moderation-confirm-modal">
            <h3 className="font-display font-semibold text-xl text-slate-900">
              {t("phase2.bulkConfirmTitle")}
            </h3>
            <p className="text-sm text-slate-600">
              {t("phase2.bulkConfirmBody")
                .replace("{n}", selectedIds.size)
                .replace("{status}", t(`phase2.view${bulkAction === "test_qa" ? "TestQa" : bulkAction === "active" ? "Production" : bulkAction.charAt(0).toUpperCase() + bulkAction.slice(1)}`))}
            </p>
            {bulkPreviewLoading && (
              <div className="text-xs text-slate-500" data-testid="bulk-preview-loading">{t("common.loading")}</div>
            )}
            {bulkPreview && (
              <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-700 space-y-1" data-testid="bulk-preview-counts">
                <div>{t("phase2.bulkPreviewUsers").replace("{n}", bulkPreview.expanded_user_count)}</div>
                {bulkPreview.duplicate_count > 0 && (
                  <div className="text-amber-700">{t("phase2.bulkPreviewDuplicates").replace("{n}", bulkPreview.duplicate_count)}</div>
                )}
                <div>{t("phase2.bulkPreviewResponses").replace("{r}", bulkPreview.response_count).replace("{s}", bulkPreview.research_count)}</div>
                <div className="text-slate-500 truncate" title={(bulkPreview.emails_covered || []).join(", ")}>
                  {t("phase2.bulkPreviewEmails").replace("{n}", (bulkPreview.emails_covered || []).length)}
                </div>
              </div>
            )}
            {/* Phase A17-J — structured audit reason dropdown.
                Internal codes are stable (never localized). UI labels
                pulled from translations. "Other" reveals a textarea. */}
            <div className="space-y-2">
              <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">
                {t("phase2.auditReasonLabel")}
              </label>
              <select
                className="tsw-input"
                value={bulkReasonCode}
                onChange={(e) => setBulkReasonCode(e.target.value)}
                data-testid="bulk-moderation-reason-code"
              >
                <option value="" disabled>{t("phase2.auditReasonPlaceholder")}</option>
                {REASON_CODES.map((code) => (
                  <option key={code} value={code} data-testid={`bulk-moderation-reason-option-${code}`}>{t(`phase2.auditReason_${code}`)}</option>
                ))}
              </select>
              {bulkReasonCode === "other" && (
                <textarea
                  className="tsw-input"
                  rows={2}
                  placeholder={t("phase2.auditReasonOtherPlaceholder")}
                  value={bulkReasonOther}
                  onChange={(e) => setBulkReasonOther(e.target.value)}
                  data-testid="bulk-moderation-reason-other"
                />
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="px-4 py-1.5 bg-slate-100 text-slate-700 rounded-full hover:bg-slate-200"
                onClick={() => setBulkConfirmOpen(false)}
                data-testid="bulk-moderation-confirm-cancel"
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                className="px-4 py-1.5 bg-emerald-600 text-white rounded-full font-medium hover:bg-emerald-700 disabled:opacity-50"
                onClick={applyBulk}
                disabled={bulkSubmitting}
                data-testid="bulk-moderation-confirm-apply"
              >
                {bulkSubmitting ? t("common.loading") : t("phase2.bulkConfirmApply")}
              </button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="edit-user-modal">
            <h3 className="font-display font-semibold text-xl text-slate-900">{editing.email}</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="tsw-label">{t("admin.notes")}</label>
                <textarea className="tsw-input min-h-[100px]" value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} data-testid="edit-notes" />
              </div>
              <div>
                <label className="tsw-label">{t("admin.tags")} ({t("common.commaSeparated")})</label>
                <input className="tsw-input" value={(editing.tags || []).join(",")} onChange={(e) => setEditing({ ...editing, tags: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} data-testid="edit-tags" />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button className="tsw-btn-ghost" onClick={() => setEditing(null)} data-testid="edit-cancel">{t("common.cancel")}</button>
              <button className="tsw-btn-primary" onClick={async () => { await updateUser(editing.id, { notes: editing.notes, tags: editing.tags }); setEditing(null); }} data-testid="edit-save">
                <Save className="w-4 h-4" /> {t("common.save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings/Profile Data Phase 2 — admin-only L4 editor drawer. */}
      {icfUserId && (
        <InternalControlFieldsDrawer
          userId={icfUserId}
          onClose={() => setIcfUserId(null)}
          onSaved={() => {
            // Refresh intelligence so the column reflects new lifecycle/etc.
            api.get("/admin/users/profile-data-intelligence?limit=2000")
              .then((r) => {
                setIntelligence(r.data);
                const byUser = {};
                (r.data.users || []).forEach((u) => { byUser[u.user_id] = u; });
                setIntelligenceByUser(byUser);
              })
              .catch(() => { /* non-fatal */ });
          }}
        />
      )}
    </div>
  );
};

export default AdminUsers;
